import React, { useEffect, useState } from 'react';
import './SpiritualConfetti.css';

interface ConfettiPiece {
  id: number;
  symbol: string;
  color: string;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  velocity: {
    x: number;
    y: number;
    rotation: number;
  };
}

interface SpiritualConfettiProps {
  isActive: boolean;
  duration?: number; // in milliseconds
  pieceCount?: number;
}

const SpiritualConfetti: React.FC<SpiritualConfettiProps> = ({ 
  isActive, 
  duration = 4000, 
  pieceCount = 50 
}) => {
  const [confettiPieces, setConfettiPieces] = useState<ConfettiPiece[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  // Spiritual symbols
  const symbols = ['ॐ', '卍'];
  
  // Spiritual colors - vibrant and auspicious
  const colors = [
    '#FF6B35', // Saffron
    '#FFD700', // Gold
    '#FF1493', // Deep Pink
    '#32CD32', // Lime Green
    '#FF4500', // Red Orange
    '#8A2BE2', // Blue Violet
    '#FF69B4', // Hot Pink
    '#00CED1', // Dark Turquoise
    '#FFB347', // Peach
    '#87CEEB', // Sky Blue
    '#DDA0DD', // Plum
    '#F0E68C'  // Khaki
  ];

  const createConfettiPiece = (id: number): ConfettiPiece => {
    return {
      id,
      symbol: symbols[Math.floor(Math.random() * symbols.length)],
      color: colors[Math.floor(Math.random() * colors.length)],
      x: Math.random() * window.innerWidth,
      y: -20,
      rotation: Math.random() * 360,
      scale: 0.8 + Math.random() * 0.8, // 0.8 to 1.6
      velocity: {
        x: (Math.random() - 0.5) * 4, // -2 to 2
        y: 2 + Math.random() * 3, // 2 to 5
        rotation: (Math.random() - 0.5) * 10 // -5 to 5
      }
    };
  };

  const updateConfettiPieces = (pieces: ConfettiPiece[]): ConfettiPiece[] => {
    return pieces.map(piece => {
      const newPiece = { ...piece };
      
      // Update position
      newPiece.x += newPiece.velocity.x;
      newPiece.y += newPiece.velocity.y;
      newPiece.rotation += newPiece.velocity.rotation;
      
      // Add some gravity and air resistance
      newPiece.velocity.y += 0.1; // gravity
      newPiece.velocity.x *= 0.995; // air resistance
      
      // Bounce off walls
      if (newPiece.x <= 0 || newPiece.x >= window.innerWidth) {
        newPiece.velocity.x *= -0.8;
      }
      
      return newPiece;
    }).filter(piece => piece.y < window.innerHeight + 50); // Remove pieces that fall off screen
  };

  useEffect(() => {
    if (!isActive) {
      setIsAnimating(false);
      setConfettiPieces([]);
      return;
    }

    setIsAnimating(true);
    
    // Create initial confetti pieces
    const initialPieces = Array.from({ length: pieceCount }, (_, i) => 
      createConfettiPiece(i)
    );
    setConfettiPieces(initialPieces);

    // Animation loop
    const animationInterval = setInterval(() => {
      setConfettiPieces(prev => {
        const updated = updateConfettiPieces(prev);
        
        // Add new pieces occasionally during the first part of animation
        if (updated.length < pieceCount / 2 && Math.random() < 0.1) {
          updated.push(createConfettiPiece(Date.now()));
        }
        
        return updated;
      });
    }, 16); // ~60fps

    // Stop animation after duration
    const stopTimeout = setTimeout(() => {
      setIsAnimating(false);
      // Let existing pieces fall
      setTimeout(() => {
        setConfettiPieces([]);
      }, 2000);
    }, duration);

    return () => {
      clearInterval(animationInterval);
      clearTimeout(stopTimeout);
    };
  }, [isActive, duration, pieceCount]);

  if (!isAnimating && confettiPieces.length === 0) {
    return null;
  }

  return (
    <div className="spiritual-confetti-container">
      {confettiPieces.map(piece => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: `${piece.x}px`,
            top: `${piece.y}px`,
            transform: `rotate(${piece.rotation}deg) scale(${piece.scale})`,
            color: piece.color,
          }}
        >
          {piece.symbol}
        </div>
      ))}
    </div>
  );
};

export default SpiritualConfetti;